import { generatePath, matchPath } from 'react-router-dom';

/**
 * The application's public route contract.
 *
 * Keep route patterns here and use them both when registering a route and when
 * navigating to it. That makes renaming a route a type-checked change instead
 * of leaving redirects pointing at a 404.
 */
export const routePatterns = {
    home: '/',
    signIn: '/signin',
    signUp: '/signup',
    forgotPassword: '/forgot-password',
    resetPassword: '/reset-password',
    emailConfirmation: '/email-confirmation',
    verifyEmail: '/verify-email',
    groupInviteAccept: '/invites/accept',
    homeInviteAccept: '/invites/home/accept',
    onboarding: '/onboarding',
    groups: '/groups',
    groupInvites: '/groups/invites',
    homeInvites: '/homes/invites',
    profile: '/profile',
    profileAccount: '/profile/account',
    profileHomes: '/profile/homes',
    profileNotifications: '/profile/notifications',
    profileCalendar: '/profile/calendar',
    group: '/groups/:groupId',
    groupListings: '/groups/:groupId/listings',
    groupPreferences: '/groups/:groupId/preferences',
    groupPreferencesDetails: '/groups/:groupId/preferences/details',
    groupPreferencesMine: '/groups/:groupId/preferences/mine',
    groupPreferencesMembers: '/groups/:groupId/preferences/members',
    groupBilling: '/groups/:groupId/preferences/billing',
    groupActivity: '/groups/:groupId/activity',
    groupActivityReviewIndex: '/groups/:groupId/activity/review',
    groupActivityReview: '/groups/:groupId/activity/review/:itemId',
    groupListingNew: '/groups/:groupId/listings/new',
    groupListing: '/groups/:groupId/listings/:listingId',
    groupListingEdit: '/groups/:groupId/listings/:listingId/edit',
    homeWorkspace: '/home/:homeId',
    homePreferences: '/home/:homeId/preferences',
    homePreferencesGeneral: '/home/:homeId/preferences/general',
    homePreferencesLocation: '/home/:homeId/preferences/location',
    homePreferencesProperty: '/home/:homeId/preferences/property',
    homePreferencesMembers: '/home/:homeId/preferences/members',
    homePreferencesBooking: '/home/:homeId/preferences/booking',
    homePreferencesFinance: '/home/:homeId/preferences/finance',
    homePreferencesCategories: '/home/:homeId/preferences/categories',
    homePreferencesIntegrations: '/home/:homeId/preferences/integrations',
    homeBilling: '/home/:homeId/preferences/billing',
    homeActivity: '/home/:homeId/activity',
    homeActivityReviewIndex: '/home/:homeId/activity/review',
    homeActivityReview: '/home/:homeId/activity/review/:itemId',
    homeActivityProcessing: '/home/:homeId/activity/processing',
    homeExpenses: '/home/:homeId/expenses',
    homeBudget: '/home/:homeId/expenses/budget',
    homeExpenseNew: '/home/:homeId/expenses/new',
    homeBankReconciliation: '/home/:homeId/expenses/reconcile',
    homeExpense: '/home/:homeId/expenses/:expenseId',
    homeExpenseEdit: '/home/:homeId/expenses/:expenseId/edit',
    homeDocuments: '/home/:homeId/documents',
    homeDocumentNew: '/home/:homeId/documents/new',
    homeDocument: '/home/:homeId/documents/:documentId',
    homeDocumentEdit: '/home/:homeId/documents/:documentId/edit',
    homeIntake: '/home/:homeId/intake/:jobId',
    homeSchedule: '/home/:homeId/schedule',
    homeInventory: '/home/:homeId/inventory',
    homeAssetNew: '/home/:homeId/inventory/new',
    homeAsset: '/home/:homeId/inventory/:assetId',
    homeAssetEdit: '/home/:homeId/inventory/:assetId/edit',
} as const;

export type RouteName = keyof typeof routePatterns;
type RoutePattern<Name extends RouteName> = (typeof routePatterns)[Name];

type PathParamNames<Path extends string> =
    Path extends `${string}:${infer Rest}`
    ? Rest extends `${infer Param}/${infer Tail}`
    ? Param | PathParamNames<`/${Tail}`>
    : Rest
    : never;

type RouteParams<Path extends string> = {
    [Param in PathParamNames<Path>]: string;
};

type RouteArguments<Path extends string> = [PathParamNames<Path>] extends [never]
    ? []
    : [params: RouteParams<Path>];

/** Builds a registered URL and requires every dynamic segment at compile time. */
export function route<Name extends RouteName>(
    name: Name,
    ...args: RouteArguments<RoutePattern<Name>>
): string {
    // `Name` is a generic union here; the public signature above enforces its
    // exact parameter shape before this shared implementation is reached.
    return generatePath(routePatterns[name], args[0] as never);
}

/** Builds a route using parameters inherited from the current matched route. */
export function routeFromCurrentParams(name: RouteName, params: Record<string, string | undefined>): string {
    return generatePath(routePatterns[name], params as never);
}

type SearchValue = string | number | boolean | null | undefined;

/** Adds query parameters without hand-assembling a fragile URL string. */
export function routeWithSearch<Name extends RouteName>(
    name: Name,
    ...args: [PathParamNames<RoutePattern<Name>>] extends [never]
        ? [params: undefined, search: Record<string, SearchValue>]
        : [params: RouteParams<RoutePattern<Name>>, search: Record<string, SearchValue>]
): string {
    const [params, search] = args;
    const query = new URLSearchParams();

    for (const [key, value] of Object.entries(search)) {
        if (value !== undefined && value !== null) {
            query.set(key, String(value));
        }
    }

    const pathname = generatePath(routePatterns[name], params as never);
    const serializedQuery = query.toString();
    return serializedQuery ? `${pathname}?${serializedQuery}` : pathname;
}

/**
 * Checks a runtime redirect (query parameter, history state, or API response)
 * against the same route contract. These values cannot be type-checked because
 * they originate outside TypeScript.
 */
export function isRegisteredRoute(destination: string | null | undefined): destination is string {
    if (!destination || !destination.startsWith('/') || destination.startsWith('//')) {
        return false;
    }

    const pathname = destination.split(/[?#]/, 1)[0];
    return Object.values(routePatterns).some((pattern) =>
        matchPath({ path: pattern, end: true }, pathname),
    );
}

/** Returns a known-good fallback when a runtime redirect is not registered. */
export function registeredRouteOr(
    destination: string | null | undefined,
    fallback: string = route('home'),
): string {
    return isRegisteredRoute(destination) ? destination : fallback;
}
